// Comms provider: polls, poll items, comm items
// Poll and comm item data supports per-piid access (home + partner sites)
//
import PropTypes from 'prop-types';
import {
  useReducer,
  useCallback,
  useMemo,
} from 'react';

import {
  config,
  displayJson,
  logDev,
  logError,
} from '../../utils';
import useAuth from '../auth/useAuth';

import CommsContext from './CommsContext';

const initialState = {
  pollsByPiid: { home: [] },
  pollsStatusByPiid: { home: 'init' },
  configuredPollItemsStatus: 'init',
  configuredPollItems: undefined,
  commItemsByPiid: { home: [] },
  commItemsStatusByPiid: { home: 'init' },
  feedbacksAll: [],
  feedbacksAllStatus: 'init',
  feedbacksByCommId: {},
  feedbacksStatusByCommId: {},
};

const pollsKey = (piid) => ((!piid || (piid === 'home')) ? 'home' : piid);

const stateReducer = (state, action) => {
  // logDev(`CommsProvider action ${action.type}`);
  switch (action.type) {
    case 'reset':
      return initialState;

    case 'setPollsByPiidStatus': {
      const key = pollsKey(action.piid);
      return ({
        ...state,
        pollsStatusByPiid: { ...state.pollsStatusByPiid, [key]: action.value },
      });
    }

    case 'setPollsByPiid': {
      const key = pollsKey(action.piid);
      return ({
        ...state,
        pollsByPiid: { ...state.pollsByPiid, [key]: action.polls },
        pollsStatusByPiid: { ...state.pollsStatusByPiid, [key]: 'ready' },
      });
    }

    case 'setPollItemsStatus':
      return ({
        ...state,
        configuredPollItemsStatus: action.value,
      });

    case 'setPollItems':
      return ({
        ...state,
        configuredPollItems: action.newConfiguredPollItemsArray,
        configuredPollItemsStatus: 'ready',
      });

    case 'setCommItemsByPiidStatus': {
      const key = pollsKey(action.piid);
      return ({
        ...state,
        commItemsStatusByPiid: { ...state.commItemsStatusByPiid, [key]: action.value },
      });
    }

    case 'setCommItemsByPiid': {
      const key = pollsKey(action.piid);
      return ({
        ...state,
        commItemsByPiid: { ...state.commItemsByPiid, [key]: action.commItems },
        commItemsStatusByPiid: { ...state.commItemsStatusByPiid, [key]: 'ready' },
      });
    }

    case 'setFeedbacksAllStatus':
      return ({ ...state, feedbacksAllStatus: action.value });

    case 'setFeedbacksAll':
      return ({
        ...state,
        feedbacksAll: action.feedbacks,
        feedbacksAllStatus: 'ready',
      });

    case 'setFeedbacksByCommIdStatus':
      return ({
        ...state,
        feedbacksStatusByCommId: {
          ...state.feedbacksStatusByCommId,
          [action.commId]: action.value,
        },
      });

    case 'setFeedbacksByCommId':
      return ({
        ...state,
        feedbacksByCommId: {
          ...state.feedbacksByCommId,
          [action.commId]: action.feedbacks,
        },
        feedbacksStatusByCommId: {
          ...state.feedbacksStatusByCommId,
          [action.commId]: 'ready',
        },
      });

    default:
      logError(
        `CommsProvider unknown action=${displayJson(action)}`,
        'CommsProvider.reducer.unknownAction',
        { actionType: action?.type },
      );
      throw new Error();
  }
};

function CommsProvider(props) {
  const {
    children,
  } = props;
  const { auth } = useAuth();
  const {
    cstmrPost,
    info,
  } = auth;
  const rlid = info?.rlid;
  const [state, dispatch] = useReducer(stateReducer, initialState);

  const refreshPolls = useCallback(
    async (piid) => {
      const key = pollsKey(piid);
      let error;
      let res;

      dispatch({
        type: 'setPollsByPiidStatus',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          rlid,
          urlPath: config.town.back.poll.list,
          payload: {},
        };
        if (key !== 'home') postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        dispatch({ type: 'setPollsByPiid', piid: key, polls: res.body.rslt.data });
        return;
      }
      logError(
        `refreshPolls(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CommsProvider.refreshPolls.error',
        {
          piid: key,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      dispatch({
        type: 'setPollsByPiidStatus',
        piid: key,
        value: 'error',
      });
    },
    [cstmrPost, rlid],
  );

  const refreshConfiguredPollItems = useCallback(
    async () => {
      let error;
      let newConfiguredPollItemsArray;
      dispatch({
        type: 'setPollItemsStatus',
        value: 'fetch',
      });

      try {
        const res = await cstmrPost({
          rlid,
          urlPath: config.town.back.poll.items.list,
          payload: {},
        });
        if (res.status !== 200) {
          throw new Error(`refreshPollItems status=${res.status}`);
        }

        newConfiguredPollItemsArray = res.body.rslt.data;
      } catch (err) {
        error = err;
      }

      if (!error) {
        dispatch({
          type: 'setPollItems',
          newConfiguredPollItemsArray,
        });

        logDev('refreshConfiguredPollItems success');
        return;
      }

      logError(
        `refreshPollItems Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CommsProvider.refreshPollItems.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      dispatch({
        type: 'setPollItemsStatus',
        value: 'error',
      });
    },
    [cstmrPost, rlid],
  );

  const getSinglePollInfo = useCallback(
    async (pollId, piid) => {
      let error;
      let pollInfo;
      try {
        const res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.poll.info,
          payload: { pollId },
        });
        if (res.status !== 200) {
          throw new Error(`getSinglePollInfo status=${res.status}`);
        }

        pollInfo = res.body.rslt.data[0];
      } catch (err) {
        error = err;
      }

      if (!error) {
        return ([null, pollInfo]);
      }

      return ([error]);
    },
    [cstmrPost, rlid],
  );

  const refreshCommItems = useCallback(
    async (piid) => {
      const key = pollsKey(piid);
      let error;
      let res;

      dispatch({
        type: 'setCommItemsByPiidStatus',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          rlid,
          urlPath: config.town.back.comm.list,
          payload: {},
        };
        if (key !== 'home') postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        dispatch({
          type: 'setCommItemsByPiid',
          piid: key,
          commItems: res.body.rslt.data,
        });
        return;
      }
      logError(
        `refreshCommItems(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CommsProvider.refreshCommItems.error',
        {
          piid: key,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      dispatch({
        type: 'setCommItemsByPiidStatus',
        piid: key,
        value: 'error',
      });
    },
    [cstmrPost, rlid],
  );

  const getSingleCommItemInfo = useCallback(
    async (commId, piid) => {
      let error;
      let commItemInfo;
      try {
        const res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.comm.info,
          payload: { commId },
        });
        if (res.status !== 200) {
          throw new Error(`getSingleCommItemInfo status=${res.status}`);
        }

        commItemInfo = res.body.rslt.data[0];
      } catch (err) {
        error = err;
      }

      if (!error) {
        return ([null, commItemInfo]);
      }

      return ([error]);
    },
    [cstmrPost, rlid],
  );

  const refreshFeedbacks = useCallback(
    async ({ commId, piid } = {}) => {
      let error;
      let res;

      if (commId !== undefined) {
        dispatch({
          type: 'setFeedbacksByCommIdStatus',
          commId,
          value: 'fetch',
        });
      } else {
        dispatch({
          type: 'setFeedbacksAllStatus',
          value: 'fetch',
        });
      }

      try {
        const payload = {};
        if (commId !== undefined) payload.commId = commId;
        const postArgs = {
          rlid,
          urlPath: config.town.back.comm.feedback.list,
          payload,
        };
        if (piid) postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        if (commId !== undefined) {
          dispatch({
            type: 'setFeedbacksByCommId',
            commId,
            feedbacks: res.body.rslt.data,
          });
        } else {
          dispatch({
            type: 'setFeedbacksAll',
            feedbacks: res.body.rslt.data,
          });
        }
        return;
      }

      logError(
        `refreshFeedbacks Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CommsProvider.refreshFeedbacks.error',
        {
          commId,
          piid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      if (commId !== undefined) {
        dispatch({
          type: 'setFeedbacksByCommIdStatus',
          commId,
          value: 'error',
        });
      } else {
        dispatch({
          type: 'setFeedbacksAllStatus',
          value: 'error',
        });
      }
    },
    [cstmrPost, rlid],
  );

  const resolveFeedback = useCallback(
    async ({
      feedbackId,
      resolutionType,
      resolutionMessage,
      piid,
    }) => {
      try {
        const postArgs = {
          rlid,
          urlPath: config.town.back.comm.feedback.resolve,
          payload: {
            feedbackId,
            resolutionType,
            resolutionMessage,
          },
        };
        if (piid) postArgs.piid = piid;

        const res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          throw new Error(`resolveFeedback status=${res.status}`);
        }
        return [null];
      } catch (err) {
        logError(
          `resolveFeedback Error:${err}`,
          'CommsProvider.resolveFeedback.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
            feedbackId,
            resolutionType,
            piid,
          },
        );
        return [err];
      }
    },
    [cstmrPost, rlid],
  );

  const contextValue = useMemo(
    () => ({
      comms: {
        refreshConfiguredPollItems,
        configuredPollItems: state.configuredPollItems,
        configuredPollItemsStatus: state.configuredPollItemsStatus,
        getSinglePollInfo,
      },
      polls: {
        byPiid: state.pollsByPiid,
        status: state.pollsStatusByPiid,
        refresh: refreshPolls,
      },
      commItems: {
        byPiid: state.commItemsByPiid,
        status: state.commItemsStatusByPiid,
        refresh: refreshCommItems,
        getSingleInfo: getSingleCommItemInfo,
      },
      feedbacks: {
        all: state.feedbacksAll,
        allStatus: state.feedbacksAllStatus,
        byCommId: state.feedbacksByCommId,
        statusByCommId: state.feedbacksStatusByCommId,
        refresh: refreshFeedbacks,
        resolve: resolveFeedback,
      },
    }),
    [
      refreshConfiguredPollItems, state.configuredPollItems, state.configuredPollItemsStatus,
      getSinglePollInfo,
      state.pollsByPiid, state.pollsStatusByPiid, refreshPolls,
      state.commItemsByPiid, state.commItemsStatusByPiid, refreshCommItems, getSingleCommItemInfo,
      state.feedbacksAll, state.feedbacksAllStatus,
      state.feedbacksByCommId, state.feedbacksStatusByCommId,
      refreshFeedbacks, resolveFeedback,
    ],
  );

  return (
    <CommsContext.Provider value={contextValue}>
      {children}
    </CommsContext.Provider>
  );
}

CommsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CommsProvider;
